<template>
  <section
    class="luce-proposition"
    aria-live="polite"
  >
    <header class="luce-proposition-titre">
      <span>Proposition de réponse</span>
      <span
        v-if="proposition.reference"
        class="luce-proposition-ref"
      >{{ proposition.reference }}</span>
    </header>

    <!-- Dit une fois, en tête : Luce n'écrit pas au client. Sans cette phrase, un agent qui
         découvre le panneau peut croire la réponse déjà partie. -->
    <p class="luce-proposition-aide">
      À relire avant envoi. Luce n'envoie rien : le texte est copié dans votre réponse, et c'est
      vous qui l'envoyez.
    </p>

    <div
      v-if="proposition.chargement"
      class="luce-proposition-attente"
    >
      <el-skeleton
        :rows="5"
        animated
      />
      <span class="luce-proposition-aide">Luce rédige une proposition…</span>
    </div>

    <template v-else>
      <div
        v-if="proposition.texte"
        class="luce-proposition-texte"
      >
        {{ proposition.texte }}
      </div>

      <el-alert
        v-else-if="proposition.raison"
        :title="proposition.raison"
        type="info"
        :closable="false"
        show-icon
      />

      <div class="luce-proposition-actions">
        <el-tooltip
          :disabled="peutCopier || !proposition.texte"
          content="Ouvrez le ticket pour y copier la réponse"
          placement="top"
        >
          <!-- Une enveloppe : un bouton désactivé ne reçoit pas le survol, l'infobulle non plus. -->
          <span>
            <el-button
              type="primary"
              :disabled="!peutCopier"
              @click="handleCopier"
            >
              Copier dans la réponse
            </el-button>
          </span>
        </el-tooltip>
      </div>

      <div
        v-if="proposition.motif !== 'sans_attente' && proposition.motif !== 'sans_consigne'"
        class="luce-proposition-regenerer"
      >
        <el-input
          v-model="indication"
          size="small"
          maxlength="300"
          placeholder="Indication facultative : plus court, plus formel…"
          @keyup.enter="handleRegenerer"
        />
        <el-button
          size="small"
          @click="handleRegenerer"
        >
          Régénérer
        </el-button>
      </div>
    </template>
  </section>
</template>

<script setup>
import { ref } from "vue";
import { ElMessage } from "element-plus";
import { useLuceProposition } from "@/composables/useLuceProposition";

/**
 * L'onglet « Proposition » du panneau de Luce : le brouillon de réponse d'un ticket.
 *
 * Aucun bouton n'envoie. Le seul geste possible est de copier le texte dans la zone de réponse
 * de la fiche ouverte — l'envoi reste celui de l'agent, sous son nom.
 */

const { proposition, peutCopier, proposer, copier } = useLuceProposition();
const indication = ref("");

function handleCopier() {
  if (copier()) {
    ElMessage.success("Proposition copiée dans votre réponse : relisez-la avant d'envoyer.");
  }
}

function handleRegenerer() {
  if (proposition.chargement || proposition.ticketId === null) return;
  proposer(proposition.ticketId, {
    reference: proposition.reference,
    indication: indication.value.trim() || null,
    regenerer: true,
  });
}
</script>

<style scoped>
.luce-proposition {
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
  min-height: 0;
  padding: 16px;
  overflow-y: auto;
}
.luce-proposition-titre {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  font-weight: 600;
  color: var(--el-text-color-primary);
}
.luce-proposition-ref {
  font-size: 12px;
  font-weight: 400;
  color: var(--el-text-color-secondary);
}
.luce-proposition-aide {
  margin: 0;
  font-size: 12px;
  line-height: 1.5;
  color: var(--el-text-color-secondary);
}
.luce-proposition-attente {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.luce-proposition-texte {
  padding: 12px;
  border-radius: 8px;
  border-left: 3px solid var(--el-color-primary);
  background: var(--el-fill-color-lighter);
  color: var(--el-text-color-regular);
  line-height: 1.55;
  white-space: pre-wrap;
  word-break: break-word;
}
.luce-proposition-actions {
  display: flex;
  justify-content: flex-end;
}
.luce-proposition-regenerer {
  display: flex;
  gap: 8px;
}
</style>
